<template>
  <div class="news-side">
    <div class="news-header">
      <h3>资讯</h3>
      <button 
        class="refresh-btn" 
        @click="handleRefresh" 
        :disabled="loading"
        title="刷新资讯"
      >
        <span :class="{ 'rotating': loading }">↻</span>
      </button>
    </div>
    <div v-if="loading && newsInfoList.length === 0" class="loading">
      <span class="loading-icon">●</span> 正在获取资讯，请稍候...
    </div>
    <div v-else-if="error" class="error">
      {{ error }}
      <button class="retry-btn" @click="fetchNewsInfo">重试</button>
    </div>
    <div v-else-if="newsInfoList.length === 0" class="empty">暂无资讯数据</div>
    <div v-else class="news-list-container">
      <ul class="news-side-list">
        <li v-for="(news) in newsInfoList" :key="news.news_id">
          <div class="news-time">{{ formatTime(news.create_time) }}</div>
          <div class="news-content">{{ getNewsContent(news) }}</div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { newMsgApi } from '@/api/index';
export default {
  name: 'NewsSide',
  data() {
    return {
      newsInfoList: [],
      loading: false,
      error: '',
      autoRefreshTimer: null  // 自动刷新定时器
    };
  },
  created() {
    this.fetchNewsInfo();
    // 启动自动刷新，每10分钟（600000毫秒）刷新一次
    this.startAutoRefresh();
  },
  beforeUnmount() {
    // 组件销毁时清除定时器
    this.stopAutoRefresh();
  },
  methods: {
    async fetchNewsInfo() {
      try {
        this.loading = true;
        this.error = '';
        const params = {
          page_idx: 1,
          page_size: 10,
          news_status: 10,
          section_id: 22
        };
        // 设置10秒超时
        // 注意：拦截器已处理响应，成功时直接返回 res.data（即 {list: [...], row_total: ...}）
        const response = await newMsgApi.getNews(params, { timeout: 10000 });
        
        // 拦截器已处理 code 检查，这里直接使用返回的核心数据
        if (!response || !Array.isArray(response.list)) {
          throw new Error('接口返回数据格式错误');
        }

        this.newsInfoList = response.list;

        if (this.newsInfoList.length > 0) {
          console.log('第一个新闻标题：', this.getNewsTitle(this.newsInfoList[0]));
        }
      } catch (err) {
        if (err.code === 'ECONNABORTED') {
          this.error = '请求超时，请稍后重试';
        } else {
          this.error = err.message || '网络异常，获取资讯失败';
        }
        console.error('请求失败：', err);
      } finally {
        this.loading = false;
      }
    },
    getNewsTitle(news) {
      if (!news || typeof news !== 'object') {
        return '暂无标题';
      }

      // 优先显示 news_txt
      let pureText = '';
      if (news.news_txt && typeof news.news_txt === 'string') {
        pureText = news.news_txt
          .replace(/<[^>]+>/g, '')  // 移除HTML标签
          .replace(/\u200b|\u00a0| +/g, ' ')  // 移除零宽空格和多余空白
          .trim();
      }

      if (pureText) {
        return pureText.length > 50 ? pureText.substring(0, 50) + '...' : pureText;
      }

      // 如果 news_txt 为空，尝试使用公司名称
      return news.comp_name ? `${news.comp_name}` : '暂无标题';
    },

    // 获取完整的 news_txt 内容（不截断，移除HTML标签）
    getNewsContent(news) {
      if (!news || typeof news !== 'object') {
        return '暂无内容';
      }

      if (news.news_txt && typeof news.news_txt === 'string') {
        return news.news_txt
          .replace(/<[^>]+>/g, '')  // 移除HTML标签
          .replace(/\u200b|\u00a0/g, '')  // 移除零宽空格
          .trim();
      }

      return news.comp_name || '暂无内容';
    },

    // 格式化时间
    formatTime(time) {
      if (!time) return '';
      // 如果是日期字符串，截取前10位（YYYY-MM-DD）或前16位（YYYY-MM-DD HH:mm）
      if (typeof time === 'string') {
        return time.length > 16 ? time.substring(0, 16) : time;
      }
      return time;
    },

    // 刷新资讯
    async handleRefresh() {
      if (this.loading) return;
      console.log('[NewsSide] 手动刷新资讯');
      await this.fetchNewsInfo();
    },

    // 启动自动刷新
    startAutoRefresh() {
      // 每10分钟（600000毫秒）自动刷新一次
      this.autoRefreshTimer = setInterval(() => {
        console.log('[NewsSide] 自动刷新资讯（10分钟定时）');
        this.fetchNewsInfo();
      }, 600000); // 10分钟 = 10 * 60 * 1000 = 600000毫秒
    },

    // 停止自动刷新
    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
        console.log('[NewsSide] 已停止自动刷新定时器');
      }
    }
  }
};
</script>

<style scoped>
.news-side {
  width: 200px;
  padding: 20px;
  padding-bottom: 60px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  height: calc(100vh - 140px);
  max-height: calc(100vh - 140px);
  text-align: left;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
}

/* 自定义滚动条样式 */
.news-side::-webkit-scrollbar {
  width: 6px;
}

.news-side::-webkit-scrollbar-track {
  background: transparent;
}

.news-side::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
  transition: background-color 0.3s;
}

.news-side::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

.news-list-container::-webkit-scrollbar {
  width: 6px;
}

.news-list-container::-webkit-scrollbar-track {
  background: transparent;
}

.news-list-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
  transition: background-color 0.3s;
}

.news-list-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

.news-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.news-header h3 {
  margin: 0;
}

.refresh-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #e0e0e0;
  border-radius: 50%;
  background: #fff;
  color: #666;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  padding: 0;
}

.refresh-btn:hover:not(:disabled) {
  background: #f5f5f5;
  color: #007bff;
  border-color: #007bff;
}

.refresh-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.refresh-btn .rotating {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.news-list-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.news-side-list {
  list-style: none;
  padding: 0;
  margin: 0;
  padding-right: 8px;
}

.news-side-list li {
  margin-bottom: 16px;
  border-bottom: 1px dashed #eee;
  padding-bottom: 16px;
}

.news-side-list li:last-child {
  margin-bottom: 0;
  border-bottom: none;
  padding-bottom: 0;
}

.news-time {
  font-size: 12px;
  color: #999;
  margin-bottom: 6px;
}

.news-content {
  color: #333;
  text-decoration: none;
  font-size: 14px;
  display: block;
  transition: color 0.2s;
  line-height: 1.6;
  word-break: break-all;
  white-space: pre-wrap;
}

.news-content:hover {
  color: #007bff;
}

.loading {
  font-size: 14px;
  color: #666;
  padding: 20px 0;
  text-align: center;
}
.loading-icon {
  display: inline-block;
  animation: spin 1s linear infinite;
  margin-right: 6px;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  font-size: 14px;
  color: #ff4444;
  padding: 20px 0;
  text-align: center;
}
.retry-btn {
  margin-top: 8px;
  padding: 4px 12px;
  border: 1px solid #007bff;
  border-radius: 4px;
  background: #fff;
  color: #007bff;
  font-size: 12px;
  cursor: pointer;
}
.retry-btn:hover {
  background: #007bff;
  color: #fff;
}

.empty {
  font-size: 14px;
  color: #999;
  padding: 20px 0;
  text-align: center;
}
</style>